import { toast } from 'sonner';

import { DownloadToast } from '~/components/common/download-toast';

import { extractApiError } from './extract-api-error';

/** Sonner options shared by every emit so re-emits replace the toast in place. */
const TOAST_OPTIONS = {
  // `flex-1` stretches Sonner's content wrapper to the toast's full width —
  // without it the wrapper shrinks to its widest text line, leaving the
  // actions floating in empty space (same fix as the export progress toast).
  classNames: { content: 'flex-1 min-w-0' },
} as const;

export interface DownloadWithToastOptions {
  /** Stable per-download identity; scopes the sonner toast id so re-runs reuse one toast. */
  key: string;
  /** Human label for the toast body (e.g. "Results Readout Deck"). */
  label: string;
  /** Performs the actual authenticated fetch + browser download. */
  run: () => Promise<void>;
  /** Fallback copy when the thrown error carries no usable message. */
  errorFallback?: string;
}

/**
 * Run an authenticated file download behind a progress toast, resolving `true`
 * on success and `false` on failure (the error is surfaced in the toast, not
 * rethrown, so callers don't need their own error handling).
 *
 * Deliberately imperative and module-scoped rather than store-backed: a blob
 * `GET` owns its whole lifecycle in one promise, so emitting straight to sonner
 * keeps the toast alive even if the triggering button unmounts mid-download —
 * without a third session store like exports / analytics-sync need for SSE.
 */
export async function downloadWithToast({
  key,
  label,
  run,
  errorFallback = 'Download failed. Please try again.',
}: DownloadWithToastOptions): Promise<boolean> {
  const toastId = `download-${key}`;

  // Dismiss-locked while in flight — it closes on completion, not by the user.
  toast(<DownloadToast label={label} status="downloading" />, {
    ...TOAST_OPTIONS,
    id: toastId,
    duration: Infinity,
    closeButton: false,
  });

  try {
    await run();
    toast(<DownloadToast label={label} status="done" />, {
      ...TOAST_OPTIONS,
      id: toastId,
      duration: 5000,
      closeButton: true,
    });
    return true;
  } catch (err) {
    toast(
      <DownloadToast
        label={label}
        status="failed"
        errorMessage={extractApiError(err, errorFallback)}
        onRetry={() => {
          void downloadWithToast({ key, label, run, errorFallback });
        }}
      />,
      { ...TOAST_OPTIONS, id: toastId, duration: Infinity, closeButton: true },
    );
    return false;
  }
}
